import {
    makeStyles
} from '@mui/styles';
import {
    Box, Slide, Typography, useTheme
} from '@mui/material';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import ProgressBar from '../components/progressBar';
import {
    useContext, useEffect, useMemo, useRef, useState
} from 'react';
import {
    Choice,
    DiagnosticAnswers
} from '../config/types';
import {
    DiagnosticFormContextValue,
    FormContext
} from '../../../contexts/form';
import {
    TypographyVariants, TypographyWeights
} from '../../../figmaTheme';

type Props = {
    hideProgress?: boolean,
    position: number,
    sectionKey: string,
    answerKey?: string,
    title?: string,
    subtitle?: string,
    choices?: Choice[],
    backJump?: number,
    forwardJump?: number,
    fatButtons?: boolean,
    goBack?: (jump?: number) => void,
    goNext?: (jump?: number) => void,
    blockBackButton?: boolean
};

const useStyles = makeStyles(() => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    header: {
        fontWeight: TypographyWeights.MEDIUM,
        marginBottom: '8px'
    },
    title: {
    },
    subtitle: {
        marginTop: '16px'
    },
    choices: {
        marginTop: '24px',
        display: 'flex',
        flexDirection: 'column',
        gridGap: '8px',
    },
    choice: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        minHeight: '45px',
        padding: '16px',
        cursor: 'pointer',
        transition: 'background-color ease-in-out 0.2s'
    },
    choiceRow: {
        display: 'flex',
        flexDirection: 'row',
        borderRadius: '8px',
        minHeight: '45px',
        padding: '16px',
        cursor: 'pointer',
        alignItems: 'center',
        columnGap: '16px',
        transition: 'background-color ease-in-out 0.2s'
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    choiceTitle: {
        fontWeight: TypographyWeights.SEMI_BOLD,
        transition: 'color ease-in-out 0.25s'
    },
    choiceSubtitle: {
        transition: 'color ease-in-out 0.25s'
    },
}));

export const MultipleChoiceScreen = ({
    hideProgress = false,
    position,
    sectionKey,
    answerKey: initialAnswerKey,
    title: initialTitle,
    subtitle,
    choices: initialChoices,
    backJump = 1,
    forwardJump,
    fatButtons,
    goBack,
    goNext,
    blockBackButton
}: Props): JSX.Element => {
    const formContext = useContext(FormContext);
    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);
    const classes = useStyles();
    const theme = useTheme();
    const answerKey = useMemo(() => {
        if (section?.key === 'challenge') {
            return `${section?.challengeIdentifier}-challenge_identifier`;
        }
        return initialAnswerKey;
    }, [section?.key, section?.challengeIdentifier, initialAnswerKey]);
    const challengeAnswer = useMemo(() => {
        const challengeSection = formContext?.sections.find(s => s.key === 'challenge');
        return challengeSection?.challengeOptions.find(o => o.key === formContext.answers[`${challengeSection?.challengeIdentifier}-challenge_identifier`]);
    }, [formContext?.sections, formContext?.answers]);
    const title = useMemo(() => {
        if (section?.key === 'challenge') {
            if (section?.challengeOptions?.length > 1) {
                return `Select the <span style="color:${section?.colors.main};font-weight:${TypographyWeights.BOLD}">${section?.challengeName} Challenge</span> that best fits your goals and lifestyle.`;
            } else {
                return `First, tap the tile below to opt in the <span style="color:${section?.colors.main};font-weight:${TypographyWeights.BOLD}">${section?.challengeName} Challenge</span>!`;
            }
        } else if (answerKey === 'endGoal' && challengeAnswer) {
            return `You opted for a ${challengeAnswer.title.toLowerCase()} to start. What's your <span style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};">ideal long-term outcome</span> when it comes to drinking?`;
        }
        return initialTitle;
    }, [section?.key, section?.challengeOptions, section?.colors.main, section?.challengeName, initialTitle]);
    const choices = useMemo(() => {
        if (section?.key === 'challenge') {
            return section?.challengeOptions;
        }
        return initialChoices;
    }, [section?.key, section?.challengeOptions, initialChoices]);
    const [selected, setSelected] = useState(formContext.answers[answerKey] || '');
    const [userClicked, setUserClicked] = useState(false);
    const [showButton, setShowButton] = useState(selected);
    const overrideForwardJump = useMemo(() => {
        if (answerKey === 'concern_level') return (formContext.answers as DiagnosticAnswers)?.endGoal === 'Drink Less' ? 1 : 4;
        return forwardJump;
    }, [
        answerKey,
        (formContext.answers as DiagnosticAnswers)?.endGoal,
        forwardJump
    ]);
    const [jump, setJump] = useState(choices.find(choice => choice.key === formContext.answers[answerKey])?.jump || overrideForwardJump);

    // Handle initial answer load
    useEffect(() => {
        if (!formContext.answers[answerKey]) return;

        setSelected(formContext.answers[answerKey]);
        setJump(choices.find(choice => choice.key === formContext.answers[answerKey])?.jump || overrideForwardJump);
        setShowButton(formContext.answers[answerKey]);
    }, [formContext.answers[answerKey]]);

    useEffect(() => {
        setSelected(formContext.answers[answerKey]);
        setShowButton(formContext.answers[answerKey]);
    }, [answerKey]);

    // Delay transition to next screen to allow time for color change animation
    useEffect(() => {
        if (!selected || !userClicked) return;
        setTimeout(() => {
            setAnswer();
        }, 300);
        setUserClicked(false);
    }, [userClicked]);

    const onChoiceClick = (e) => {
        setUserClicked(true);
        setSelected(e.currentTarget.dataset.key);
        setJump(parseInt(e.currentTarget.dataset.jump) || overrideForwardJump || 1);
    };

    const onNextButtonClick = () => {
        setAnswer();
        setUserClicked(false);
    };

    const setAnswer = () => {
        if (answerKey === 'endGoal') {
            (formContext as DiagnosticFormContextValue).setEndGoal({
                [answerKey]: selected
            });
        } else if (answerKey === 'customTargets') {
            (formContext as DiagnosticFormContextValue).setCustomTargets({
                [answerKey]: selected
            });
        } else if (answerKey === 'pronouns') {
            (formContext as DiagnosticFormContextValue).setPronouns({
                [answerKey]: selected
            });
        } else {
            formContext.setAnswers({
                [answerKey]: selected
            });
        }
        goNext ? goNext?.(jump) : formContext.goNext(jump);
    };

    const onBackClick = () => {
        goBack ? goBack(backJump) : formContext.goBack(backJump);
    };

    const scrollToBox = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (scrollToBox.current) {
            scrollToBox.current.scrollIntoView({
                behavior: 'smooth'
            });
        }
    }, [position]);

    return (
        <CardContainer
            sectionKey={sectionKey}
            headerText={section?.key === 'challenge' ? 'Set Up Your Challenge' : null}
            goBack={!blockBackButton && onBackClick}
            goNext={showButton && onNextButtonClick}
            hasGradient
            showCTAButton={showButton && (formContext as DiagnosticFormContextValue).useCTAs}
            ctaButtonStyle={(formContext as DiagnosticFormContextValue).CTAButtonStyle}
            ctaButtonTextStyle={(formContext as DiagnosticFormContextValue).CTAButtonTextStyle}
            ctaButtonIcon={(formContext as DiagnosticFormContextValue).CTAButtonIcon}
            ctaTextStyle={(formContext as DiagnosticFormContextValue).CTATextStyle}
            buttonText={(formContext as DiagnosticFormContextValue).CTAButtonText}
            hideArrowButton={(formContext as DiagnosticFormContextValue).useCTAs}
        >
            <Card>
                {(!(formContext as DiagnosticFormContextValue).hideProgress && !hideProgress) && (
                    <ProgressBar
                        currentPosition={position}
                        sectionKey={sectionKey}
                    />
                )}
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            <Box
                                ref={scrollToBox}
                                style={{
                                    position: 'absolute'
                                }}
                            />
                            {section?.title && (
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={classes.header}
                                    style={{
                                        color: section?.colors.main
                                    }}
                                >
                                    {section.title}
                                </Typography>
                            )}
                            {title && (
                                <Typography
                                    variant={TypographyVariants.H3}
                                    className={classes.title}
                                    dangerouslySetInnerHTML={{
                                        __html: title
                                    }}
                                />
                            )}
                            {subtitle && (
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.subtitle}
                                    dangerouslySetInnerHTML={{
                                        __html: subtitle
                                    }}
                                />
                            )}
                            <Box className={classes.choices}>
                                {choices.map(choice => (
                                    <Box
                                        key={choice.key}
                                        data-key={choice.key}
                                        data-jump={choice.jump}
                                        className={choice?.orientation === 'horizontal' ? classes.choiceRow : classes.choice}
                                        style={{
                                            padding: fatButtons ? '20px 16px' : '16px',
                                            backgroundColor: choice.key === selected ? (section?.colors.choiceConfirmed || section?.colors.main) : section?.colors.choice
                                        }}
                                        onClick={onChoiceClick}
                                    >
                                        {choice?.icon && (choice?.icon)}
                                        <Box>
                                            <Box className={classes.titleContainer}>
                                                {choice?.ctaIcon && choice.ctaIcon.position === 'left' && (choice.ctaIcon.element)}
                                                {choice?.title && (
                                                    <Typography
                                                        variant={fatButtons ? TypographyVariants.H3 : TypographyVariants.H5}
                                                        className={classes.choiceTitle}
                                                        style={{
                                                            color: choice.key === selected ? theme.palette.white : section?.colors.choiceText,
                                                            fontWeight: fatButtons ? TypographyWeights.BOLD : TypographyWeights.SEMI_BOLD
                                                        }}
                                                    >
                                                        {choice.title}
                                                    </Typography>
                                                )}
                                                {choice?.ctaIcon && choice.ctaIcon.position === 'right' &&  (choice.ctaIcon.element)}
                                            </Box>
                                            {choice?.subtitle && (
                                                <Typography
                                                    variant={TypographyVariants.H6}
                                                    className={classes.choiceSubtitle}
                                                    style={{
                                                        color: choice.key === selected ? theme.palette.white : section?.colors.choiceText
                                                    }}
                                                >
                                                    {choice.subtitle}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Slide>
                    <Box
                        style={{
                            height: '500px'
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default MultipleChoiceScreen;
